import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, ChevronRight, Play } from 'lucide-react';
import { motion } from 'motion/react';
import type { FonotekaPack, FonotekaTrack } from '@/api/client';
import { api } from '@/api/client';
import { useGlobalAudioPlayback } from '@/audio/GlobalAudioContext';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useToast } from '@/contexts/ToastContext';
import { getFileTypeLabel } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { patchProjectFonotekaTracksInCache } from './cache';
import { FONOTEKA_QUERY_KEY, projectFonotekaTracksKey } from './queryKeys';
import {
  applyPackBulk,
  initialExpandedPackId,
  packCheckState,
  packCheckboxValue,
  toggleExclusiveId,
} from './packTriState';
import { FonotekaAudioPlayer } from './FonotekaAudioPlayer';
import { FonotekaCollapsible } from './FonotekaCollapsible';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';

export type FonotekaProjectPickerProps = {
  projectId: string;
  projectName: string;
  open: boolean;
  canWrite: boolean;
  onClose?: () => void;
};

export function FonotekaProjectPicker({
  projectId,
  projectName,
  open,
  canWrite,
  onClose,
}: FonotekaProjectPickerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [expandedPackId, setExpandedPackId] = useState<string | null>(null);
  const saveGenRef = useRef(0);
  const seededExpandRef = useRef(false);

  const packsQuery = useQuery({
    queryKey: FONOTEKA_QUERY_KEY,
    queryFn: () => api.fonoteka.listPacks(),
    enabled: open,
  });
  const tracksQuery = useQuery({
    queryKey: projectFonotekaTracksKey(projectId),
    queryFn: () => api.projects.getFonotekaTracks(projectId),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setExpandedPackId(null);
      seededExpandRef.current = false;
      return;
    }
    const ids = tracksQuery.data?.track_ids;
    if (!ids) return;
    setSelected(new Set(ids));
  }, [open, tracksQuery.data]);

  useEffect(() => {
    if (!open || seededExpandRef.current || !packsQuery.data || !tracksQuery.data) return;
    seededExpandRef.current = true;
    const selectedIds = new Set(tracksQuery.data.track_ids);
    setExpandedPackId(
      initialExpandedPackId(
        packsQuery.data.map((pack) => ({
          id: pack.id,
          trackIds: pack.tracks.map((track) => track.id),
        })),
        selectedIds,
      ),
    );
  }, [open, packsQuery.data, tracksQuery.data]);

  const persistSelection = useCallback(
    async (next: Set<string>) => {
      const ids = [...next];
      const gen = ++saveGenRef.current;
      patchProjectFonotekaTracksInCache(queryClient, projectId, ids);
      try {
        const data = await api.projects.putFonotekaTracks(projectId, ids);
        if (gen !== saveGenRef.current) return;
        patchProjectFonotekaTracksInCache(queryClient, projectId, data.track_ids);
        setSelected(new Set(data.track_ids));
      } catch (err) {
        if (gen !== saveGenRef.current) return;
        showError(err instanceof Error ? err.message : 'Не удалось сохранить фонотеку');
        try {
          const fresh = await queryClient.fetchQuery({
            queryKey: projectFonotekaTracksKey(projectId),
            queryFn: () => api.projects.getFonotekaTracks(projectId),
          });
          setSelected(new Set(fresh.track_ids));
          patchProjectFonotekaTracksInCache(queryClient, projectId, fresh.track_ids);
        } catch {
          /* already toasted */
        }
      }
    },
    [projectId, queryClient, showError],
  );

  const toggleTrack = useCallback(
    (trackId: string, on: boolean) => {
      if (!canWrite) return;
      setSelected((prev) => {
        const next = new Set(prev);
        if (on) next.add(trackId);
        else next.delete(trackId);
        void persistSelection(next);
        return next;
      });
    },
    [canWrite, persistSelection],
  );

  const togglePack = useCallback(
    (packTrackIds: string[]) => {
      if (!canWrite || packTrackIds.length === 0) return;
      setSelected((prev) => {
        const state = packCheckState(packTrackIds, prev);
        const next = applyPackBulk(packTrackIds, prev, state !== 'all');
        void persistSelection(next);
        return next;
      });
    },
    [canWrite, persistSelection],
  );

  const toggleExpanded = useCallback((packId: string) => {
    setExpandedPackId((prev) => toggleExclusiveId(prev, packId));
  }, []);

  const openFonotekaPage = useCallback(() => {
    onClose?.();
    navigate('/fonoteka');
  }, [navigate, onClose]);

  const packs = packsQuery.data ?? [];
  const loading = (packsQuery.isLoading && !packsQuery.data) || (tracksQuery.isLoading && !tracksQuery.data);

  return (
    <div className="fonoteka-picker">
      <div className="fonoteka-picker__head">
        <span className="fonoteka-picker__title">
          Фонотека · {projectName.trim() || 'Без названия'}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="fonoteka-picker__open"
          aria-label="Открыть фонотеку"
          title="Открыть фонотеку"
          onClick={openFonotekaPage}
        >
          <ArrowUpRight />
        </Button>
      </div>
      <OverlayScrollArea defer={false} className="fonoteka-picker__scroll">
        {loading ? (
          <div className="fonoteka-picker__empty">Загрузка…</div>
        ) : packsQuery.isError || tracksQuery.isError ? (
          <div className="fonoteka-picker__empty fonoteka-picker__empty_error">
            Не удалось загрузить каталог
          </div>
        ) : packs.length === 0 ? (
          <div className="fonoteka-picker__empty">Проектов пока нет</div>
        ) : (
          <div className="fonoteka-picker__list">
            {packs.map((pack) => (
              <PickerPack
                key={pack.id}
                pack={pack}
                selected={selected}
                expanded={expandedPackId === pack.id}
                canWrite={canWrite}
                onToggleExpanded={() => toggleExpanded(pack.id)}
                onTogglePack={togglePack}
                onToggleTrack={toggleTrack}
              />
            ))}
          </div>
        )}
      </OverlayScrollArea>
    </div>
  );
}

function PickerPack({
  pack,
  selected,
  expanded,
  canWrite,
  onToggleExpanded,
  onTogglePack,
  onToggleTrack,
}: {
  pack: FonotekaPack;
  selected: Set<string>;
  expanded: boolean;
  canWrite: boolean;
  onToggleExpanded: () => void;
  onTogglePack: (trackIds: string[]) => void;
  onToggleTrack: (trackId: string, on: boolean) => void;
}) {
  const trackIds = pack.tracks.map((t) => t.id);
  const packState = packCheckState(trackIds, selected);
  const { track: globalTrack } = useGlobalAudioPlayback();
  const showPackPlaying =
    globalTrack?.source === 'fonoteka' && globalTrack.packId === pack.id;

  const handleHeaderKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggleExpanded();
      }
    },
    [onToggleExpanded],
  );

  return (
    <div className="fonoteka-picker__card" data-expanded={expanded ? 'true' : 'false'}>
      <div
        role="button"
        tabIndex={0}
        className="fonoteka-picker__card-head"
        aria-expanded={expanded}
        onClick={onToggleExpanded}
        onKeyDown={handleHeaderKeyDown}
      >
        <motion.span
          className="fonoteka-picker__chevron"
          animate={{ rotate: expanded ? 90 : 0 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden
        >
          <ChevronRight className="size-3.5" strokeWidth={2} />
        </motion.span>
        <span
          className="fonoteka-picker__check"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={packCheckboxValue(packState)}
            disabled={!canWrite || trackIds.length === 0}
            onCheckedChange={() => onTogglePack(trackIds)}
            aria-label={`Проект ${pack.name}`}
          />
        </span>
        <span className="fonoteka-picker__name">{pack.name}</span>
        <Badge variant="secondary">{pack.tracks.length}</Badge>
        {showPackPlaying ? (
          <span className="fonoteka-pack-playing" title="Воспроизводится из этого проекта" aria-hidden>
            <Play className="size-3.5" fill="currentColor" strokeWidth={0} />
          </span>
        ) : null}
      </div>
      <FonotekaCollapsible open={expanded}>
        {pack.tracks.length === 0 ? (
          <div className="fonoteka-picker__pack-empty">В проекте пока нет треков</div>
        ) : (
          pack.tracks.map((track) => (
            <PickerTrack
              key={track.id}
              track={track}
              checked={selected.has(track.id)}
              canWrite={canWrite}
              onToggleTrack={onToggleTrack}
            />
          ))
        )}
      </FonotekaCollapsible>
    </div>
  );
}

function PickerTrack({
  track,
  checked,
  canWrite,
  onToggleTrack,
}: {
  track: FonotekaTrack;
  checked: boolean;
  canWrite: boolean;
  onToggleTrack: (trackId: string, on: boolean) => void;
}) {
  const ext = getFileTypeLabel(track.file_name);

  return (
    <div className="fonoteka-picker__row fonoteka-picker__row_track">
      <Checkbox
        checked={checked}
        disabled={!canWrite}
        onCheckedChange={(value) => onToggleTrack(track.id, value === true)}
        aria-label={track.file_name}
      />
      <FonotekaAudioPlayer
        instanceId={track.id}
        src={track.file_url}
        labelForA11y={track.file_name}
        packId={track.pack_id}
      />
      {ext ? <Badge variant="secondary">{ext}</Badge> : null}
      <span className="fonoteka-picker__name" title={track.file_name}>
        {track.file_name}
      </span>
    </div>
  );
}
